import { useState } from "react";
import { Camera, Image as ImageIcon, Paperclip, Trash2, X, FileText, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export const PhotoGrid = ({
  photos = [], files = [], jobId, token, onAdd, onDelete, busy,
  fileRef, onFile, imageRef, onImage, docRef, onDoc, onDocDelete, readOnly = false,
}) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(null);
  const src = (p) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/photos/${p.id}?auth=${token}`;
  const docSrc = (f) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/files/${f.id}?auth=${token}`;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold text-slate-800">
          {t("photos.title")} ({photos.length + files.length})
        </h2>
        {!readOnly && (<div className="flex flex-wrap gap-2">
          <Button
            data-testid="work-order-add-photo"
            onClick={onAdd}
            disabled={busy}
            className="h-10 bg-slate-900 text-white transition-colors duration-200 hover:bg-slate-800"
          >
            <Camera className="mr-1.5 h-4 w-4" /> {t("photos.takePhoto")}
          </Button>
          <Button
            data-testid="work-order-add-image"
            onClick={() => imageRef?.current?.click()}
            disabled={busy}
            variant="outline"
            className="h-10 bg-white transition-colors duration-200 hover:bg-slate-100"
          >
            <ImageIcon className="mr-1.5 h-4 w-4" /> {t("photos.pickImage")}
          </Button>
          <Button
            data-testid="work-order-add-file"
            onClick={() => docRef?.current?.click()}
            disabled={busy}
            variant="outline"
            className="h-10 bg-white transition-colors duration-200 hover:bg-slate-100"
          >
            <Paperclip className="mr-1.5 h-4 w-4" /> {t("photos.pickFile")}
          </Button>
        </div>)}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFile}
        data-testid="work-order-photo-input"
        className="hidden"
      />
      <input
        ref={imageRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onImage}
        data-testid="work-order-image-input"
        className="hidden"
      />
      <input
        ref={docRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.zip,application/pdf"
        multiple
        onChange={onDoc}
        data-testid="work-order-file-input"
        className="hidden"
      />

      {photos.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500" data-testid="work-order-photos-empty">
          {t("photos.empty")}
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
          {photos.map((p) => (
            <div key={p.id} className="relative" data-testid={`work-order-photo-${p.id}`}>
              <button
                data-testid={`work-order-photo-open-${p.id}`}
                onClick={() => setZoom(p)}
                className="block w-full overflow-hidden rounded-md border border-slate-200 transition-colors duration-200 hover:border-blue-400"
              >
                <img src={src(p)} alt={p.filename || "Jobbfoto"} className="aspect-square w-full object-cover" />
              </button>
              {!readOnly && (<button
                data-testid={`work-order-photo-delete-${p.id}`}
                onClick={() => onDelete(p.id)}
                className="absolute right-0.5 top-0.5 rounded bg-white/90 p-1 text-red-600 transition-colors duration-200 hover:bg-white"
              >
                <Trash2 className="h-3 w-3" />
              </button>)}
            </div>
          ))}
        </div>
      )}

      <h3 className="mt-4 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">
        {t("photos.filesTitle")} ({files.length})
      </h3>
      {files.length === 0 ? (
        <p className="mt-1 text-sm text-slate-500" data-testid="work-order-files-empty">
          {t("photos.filesEmpty")}
        </p>
      ) : (
        <div className="mt-2 space-y-1" data-testid="work-order-files">
          {files.map((f) => (
            <div
              key={f.id}
              data-testid={`work-order-file-${f.id}`}
              className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-2.5"
            >
              <FileText className="h-4 w-4 shrink-0 text-blue-600" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                {f.filename || f.id}
              </span>
              <a
                data-testid={`work-order-file-open-${f.id}`}
                href={docSrc(f)}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded p-2 text-blue-600 transition-colors duration-200 hover:bg-blue-50"
                title={t("common.download")}
              >
                <Download className="h-4 w-4" />
              </a>
              {!readOnly && (<button
                data-testid={`work-order-file-delete-${f.id}`}
                onClick={() => onDocDelete(f.id)}
                className="rounded p-2 text-red-600 transition-colors duration-200 hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>)}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!zoom} onOpenChange={() => setZoom(null)}>
        <DialogContent data-testid="photo-lightbox" className="max-w-3xl bg-black p-2">
          {zoom && <img src={src(zoom)} alt={zoom.filename || "Jobbfoto"} className="max-h-[80vh] w-full object-contain" />}
          <button
            onClick={() => setZoom(null)}
            data-testid="photo-lightbox-close"
            className="absolute right-2 top-2 rounded bg-white/90 p-1.5 text-slate-900"
          >
            <X className="h-4 w-4" />
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoGrid;
